import { useEffect, useState, Fragment, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ExportExcelButton } from "@/components/ExportExcelButton";
import {
  Plus, X, Search, ChevronDown, ChevronRight,
  Coins, ShieldAlert, Trash2, Save, FileText, Building2, History,
  Banknote, AlertCircle, FileDown,
} from "lucide-react";
import { BranchFilter } from "@/components/BranchFilter";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { useTabReturnRefresh } from "@/hooks/useTabReturnRefresh";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { PaginationControls } from "@/components/PaginationControls";
import { SearchableSelect } from "@/components/SearchableSelect";

const MODULE_LABEL = {
  purchase_invoice: "Purchase Invoice",
  store_rent: "Store Rent",
  salary_payment: "Salary",
  general_payment: "General Payment",
};
const DEDUCTION_TYPES = ["LD", "Damage Recovery", "Other Recovery"];
const HOLD_TYPES = ["GST Hold", "Retention Hold", "Other Hold"];

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

async function downloadPaymentAdvice(v) {
  try {
    const r = await api.get(`/bill-payment-vouchers/${v.id}/payment-advice-pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-advice-${(v.voucher_number || v.id).replace(/[\\/]/g, "-")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success("Payment Advice downloaded");
  } catch (e) {
    let detail = "Download failed";
    if (e?.response?.data instanceof Blob) {
      try { detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* default */ }
    } else if (e?.response?.data?.detail) {
      detail = e.response.data.detail;
    }
    toast.error(detail);
  }
}


function badge(status) {
  const cls = {
    "Pending":  "bg-amber-100 text-amber-800",
    "Approved": "bg-emerald-100 text-emerald-800",
    "Posted":   "bg-indigo-100 text-indigo-800",
    "Paid":     "bg-emerald-200 text-emerald-900",
    "Rejected": "bg-rose-100 text-rose-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <span className={`text-[10px] font-bold uppercase tracking-wider ${cls} px-1.5 py-0.5`}>{status}</span>;
}

export default function BillPaymentPage() {
  const [tab, setTab] = useState("vouchers");          // 'vouchers' | 'outstandings' | 'advances'
  const [advances, setAdvances] = useState([]);  // legacy mirror for non-paginated callers (NewVoucherForm etc.)
  const [outVendors, setOutVendors] = useState([]);      // [{vendor_id, vendor_code, vendor_name, outstanding_count, ambiguous}]
  const [outVendor, setOutVendor] = useState("");        // selected vendor display name
  const [outVendorId, setOutVendorId] = useState("");    // selected vendor id (identity key)
  const [outVendorKey, setOutVendorKey] = useState("");  // picker key (vendor_id or name:: fallback)
  const [outBills, setOutBills] = useState([]);          // outstanding bills for selected vendor
  const [outLoading, setOutLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [advSearch, setAdvSearch] = useState("");
  const [outSearch, setOutSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [filterBranch, setFilterBranch] = useState("");
  const [showNew, setShowNew] = useState(true);   // land on New Voucher form
  const [formType, setFormType] = useState("bill_payment"); // 'bill_payment' | 'advance_payment'
  const [newKey, setNewKey] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [viewingAdv, setViewingAdv] = useState(null);

  // Server-side paginated lists. Status, branch AND free-text search are
  // pushed to the API (debounced) — client-only search silently missed
  // vouchers sitting on other pages once volume grew past one page.
  const debouncedSearch = useDebouncedValue(search, 350);
  const debouncedAdvSearch = useDebouncedValue(advSearch, 350);
  const voucherParams = useMemo(() => {
    const p = {};
    if (filterBranch) p.branch_id = filterBranch;
    if (statusFilter && statusFilter !== "ALL") p.status = statusFilter;
    if (debouncedSearch.trim()) p.search = debouncedSearch.trim();
    return p;
  }, [filterBranch, statusFilter, debouncedSearch]);
  const {
    items: vouchers, total: vouchersTotal,
    page: vouchersPage, pageSize: vouchersPageSize,
    loading: vouchersLoading,
    setPage: setVouchersPage, setPageSize: setVouchersPageSize,
    reload: reloadVouchers,
  } = usePaginatedList("/bill-payment-vouchers", voucherParams);

  const advanceParams = useMemo(() => {
    const p = {};
    if (filterBranch) p.branch_id = filterBranch;
    if (debouncedAdvSearch.trim()) p.search = debouncedAdvSearch.trim();
    return p;
  }, [filterBranch, debouncedAdvSearch]);
  const {
    items: paginatedAdvances, total: advancesTotal,
    page: advancesPage, pageSize: advancesPageSize,
    loading: advancesLoading,
    setPage: setAdvancesPage, setPageSize: setAdvancesPageSize,
    reload: reloadAdvances,
  } = usePaginatedList("/vendor-advances", advanceParams);

  // Keep the legacy `advances` mirror in sync so existing detail-modal lookups
  // (which expect the full advance list) keep working without re-fetch.
  useEffect(() => { setAdvances(paginatedAdvances || []); }, [paginatedAdvances]);

  const loadVouchers = reloadVouchers;
  const loadAdvances = reloadAdvances;
  // Jun-2026 — vendors/outstandings auto-refresh when an approval lands
  // elsewhere (masters:refresh fired by the Approvals Inbox) and when the
  // user returns to this tab (TabContent keeps pages mounted, so one-shot
  // effects never re-fire on tab switches).
  const mastersRefreshKey = useMastersRefresh();
  const [outTick, setOutTick] = useState(0);
  const tabRootRef = useTabReturnRefresh(() => setOutTick(t => t + 1));
  const loadOutVendors = () => api.get("/bill-payments/vendors-with-outstanding").then(r => setOutVendors(r.data || []));
  useEffect(() => { loadOutVendors(); }, [mastersRefreshKey, outTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!outVendorKey) { setOutBills([]); return; }
    setOutLoading(true);
    // Jul-2026 — VENDOR IDENTITY FIX: fetch outstandings by vendor_id so two
    // vendors sharing a display name are never merged.
    api.get("/bill-payments/outstanding", { params: { vendor_id: outVendorId, vendor_name: outVendor } })
      .then(r => setOutBills(r.data || []))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load outstandings"))
      .finally(() => setOutLoading(false));
  }, [outVendorKey, mastersRefreshKey, outTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local search filter — operates only on the current page since the
  // backend doesn't support free-text search on these voucher fields.
  const filtered = vouchers.filter(v => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [v.voucher_number, v.vendor_name, v.notes, v.posted_batch_number]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });
  const bpvPendingIds = useMemo(() => pendingDocIds(filtered), [filtered]);
  const bpvPendingMap = useApprovalPendingMap("bill_payment_voucher", bpvPendingIds);
  const advPendingIds = useMemo(() => pendingDocIds(paginatedAdvances), [paginatedAdvances]);
  const advPendingMap = useApprovalPendingMap("vendor_advance", advPendingIds);

  const filteredOuts = outBills.filter(b => {
    if (!outSearch.trim()) return true;
    const q = outSearch.trim().toLowerCase();
    return [b.source_number, b.vendor_ref_no, b.project_name, b.branch_name, b.parent_voucher_number]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });
  const vendorOutTotal = filteredOuts.reduce((s, b) => s + Number(b.net_payable || 0), 0);
  const totalOutCount = outVendors.reduce((s, v) => s + Number(v.outstanding_count || 0), 0);

  const filteredAdv = paginatedAdvances.filter(a => {
    if (!advSearch.trim()) return true;
    const q = advSearch.trim().toLowerCase();
    return [a.advance_number, a.vendor_name, a.purpose, a.notes, a.posted_batch_number]
      .some(s => (s || "").toString().toLowerCase().includes(q));
  });

  function openNewForm(kind) {
    setFormType(kind);
    setShowNew(true);
    setNewKey(k => k + 1);
  }

  return (
    <div ref={tabRootRef} className="px-4 sm:px-8 py-6 max-w-[1700px] mx-auto">
      <Toaster richColors />

      {!showNew && <>
      <div className="flex items-end gap-1 mb-4 border-b border-slate-200">
        {[
          ["vouchers", `Vouchers (${vouchersTotal})`],
          ["outstandings", `Outstandings (${totalOutCount})`],
          ["advances", `Advances (${advancesTotal})`],
        ].map(([k, l]) => (
          <button key={k} data-testid={`bp-page-tab-${k}`} onClick={() => setTab(k)}
            className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${
              tab === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
        <div className="ml-auto pb-2 flex gap-2">
          <ExportExcelButton endpoint="/exports/bill-payment-vouchers-excel"
            filename="bill-payment-vouchers.xlsx"
            label="Export Excel"
            testid="bpv-export"/>
          <button onClick={() => openNewForm("advance_payment")} data-testid="bp-new-advance-btn"
            className="inline-flex items-center gap-2 border border-[#F97316] text-[#F97316] bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-orange-50">
            <Banknote size={14}/> New Advance Payment
          </button>
          <button onClick={() => openNewForm("bill_payment")} data-testid="bp-new-voucher-btn"
            className="inline-flex items-center gap-2 bg-[#F97316] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C]">
            <Plus size={14}/> New Bill Payment Voucher
          </button>
        </div>
      </div>

      {tab === "vouchers" && (<>
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="bp-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search voucher #, vendor, batch…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
        </div>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="bp-filter-branch" />
        <div className="flex border border-slate-200">
          {["ALL", "Pending", "Approved", "Posted", "Paid", "Rejected"].map(s => (
            <button key={s} data-testid={`bp-tab-${s}`} onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold ${
                statusFilter === s ? "bg-[#F97316] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}>{s}</button>
          ))}
        </div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
          Showing {filtered.length} of {vouchersTotal} voucher{vouchersTotal !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {["Voucher #", "Date", "Vendor", "Bills", "Invoice", "GST", "TDS", "Deductions", "Holds", "Net Payable", "Status", "Paid Date", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2 whitespace-nowrap">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="py-10 text-center text-slate-400">No bill payment vouchers found. Click <strong>New Bill Payment Voucher</strong> to create one.</td></tr>
            )}
            {filtered.map(v => (
              <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setViewing(v)} data-testid={`bp-voucher-row-${v.id}`}>
                <td className="py-2 px-2 font-bold text-slate-900">{v.voucher_number || "—"}</td>
                <td className="py-2 px-2 text-slate-600">{fmtDate(v.voucher_date)}</td>
                <td className="py-2 px-2 text-slate-700">{v.vendor_name}</td>
                <td className="py-2 px-2 tabular text-slate-600">{(v.lines || []).length}</td>
                <td className="py-2 px-2 text-right tabular">{formatINR(v.total_invoice)}</td>
                <td className="py-2 px-2 text-right tabular">{formatINR(v.total_gst)}</td>
                <td className="py-2 px-2 text-right tabular text-rose-600">{formatINR(v.total_tds)}</td>
                <td className="py-2 px-2 text-right tabular text-orange-700">{v.total_deductions > 0 ? formatINR(v.total_deductions) : "—"}</td>
                <td className="py-2 px-2 text-right tabular text-amber-700">{v.total_holds > 0 ? formatINR(v.total_holds) : "—"}</td>
                <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(v.total_net_payable)}</td>
                <td className="py-2 px-2">
                  <PendingApprovalBadge status={v.status} pendingInfo={bpvPendingMap[v.id]} />
                  {v.status === "Posted" && <div className="text-[9px] text-slate-500 mt-0.5">{v.posted_batch_number}</div>}
                </td>
                <td className="py-2 px-2 text-slate-700 whitespace-nowrap text-[11px]"
                    data-testid={`bp-paid-date-${v.id}`}>
                  {v.actual_payment_date ? fmtDate(v.actual_payment_date) : <span className="text-slate-300">—</span>}
                </td>
                <td className="py-2 px-2 text-right" onClick={e => e.stopPropagation()}>
                  {bpvPendingMap[v.id] && (
                    <span className="mr-2 inline-block align-middle">
                      <TakeActionButton pendingInfo={bpvPendingMap[v.id]} testid={`take-action-bpv-${v.id}`} />
                    </span>
                  )}
                  {v.status === "Paid" && (
                    <button onClick={() => downloadPaymentAdvice(v)} data-testid={`bp-advice-${v.id}`}
                      title="Download Payment Advice (PDF)"
                      className="text-emerald-600 hover:text-emerald-800 p-1 mr-1"><FileDown size={13}/></button>
                  )}
                  {v.status === "Approved" && Math.abs(Number(v.total_net_payable || 0)) < 0.01 && !v.payment_hold && (
                    <button
                      onClick={async () => {
                        const remarks = window.prompt(
                          `Mark voucher ${v.voucher_number} as fully cleared via advance adjustment?\n\n` +
                          `Bills paid: ${formatINR(v.total_invoice + v.total_gst - v.total_tds - (v.total_deductions||0) - (v.total_holds||0))}\n` +
                          `Advance adjusted: ${formatINR(v.total_advance_adjusted)}\n` +
                          `Bank disbursement: ₹0 (no cash leaves bank)\n\n` +
                          `Optional remarks:`,
                          ""
                        );
                        if (remarks === null) return; // cancelled
                        try {
                          await api.post(`/bill-payment-vouchers/${v.id}/mark-adjusted`,
                            { remarks: remarks || "", actual_payment_date: new Date().toISOString().slice(0, 10) });
                          toast.success(`Voucher ${v.voucher_number} cleared via advance adjustment`);
                          loadVouchers();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Failed to mark as adjusted");
                        }
                      }}
                      data-testid={`bp-mark-adjusted-${v.id}`}
                      title="Voucher is fully covered by advance adjustment — click to mark Paid without a bank batch"
                      className="text-violet-600 hover:text-violet-800 p-1 mr-1 inline-flex items-center gap-1 border border-violet-300 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide hover:bg-violet-50"
                    >
                      <Coins size={11}/> Mark Adjusted
                    </button>
                  )}
                  {v.status === "Approved" && !v.payment_hold && (
                    <button onClick={async () => {
                      // Sprint 2026-Feb-10q — hard confirmation because
                      // deleting an Approved voucher reverses vendor-
                      // advance adjustments and returns the bills to
                      // creditor Outstanding.
                      if (!window.confirm(
                        `Delete Approved voucher ${v.voucher_number || ""}?\n\n` +
                        `This will:\n` +
                        `  • Reverse any advance adjustments on this voucher\n` +
                        `  • Return the source bills to Creditor Outstanding\n` +
                        `  • Allow a new Bill Payment Voucher to be created for those bills\n\n` +
                        `Proceed?`
                      )) return;
                      try {
                        const r = await api.delete(`/bill-payment-vouchers/${v.id}`);
                        const d = r?.data || {};
                        toast.success(
                          `Deleted voucher ${d.voucher_number || v.voucher_number || ""} — ` +
                          `${d.bills_restored || 0} bill(s) restored to Outstanding.`
                        );
                        loadVouchers();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Delete failed");
                      }
                    }}
                      data-testid={`bp-delete-${v.id}`}
                      title="Delete this Approved voucher — reverses adjustments and restores creditor O/S"
                      className="text-rose-600 hover:text-rose-800 p-1 inline-flex items-center gap-1 border border-rose-300 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide hover:bg-rose-50">
                      <Trash2 size={11}/> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationControls
        page={vouchersPage} pageSize={vouchersPageSize} total={vouchersTotal}
        loading={vouchersLoading}
        onPage={setVouchersPage} onPageSize={setVouchersPageSize}
      />
      </>)}

      {tab === "outstandings" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Vendor *</div>
              {/* Jul-2026 — VENDOR IDENTITY FIX: keyed by vendor_id (falls
                  back to a name:: key only for legacy rows without one) so
                  two vendors sharing a display name appear as two distinct,
                  selectable entries. */}
              <SearchableSelect
                testid="bp-out-vendor-select"
                value={outVendorKey}
                onChange={(key, row) => {
                  setOutVendorKey(key || "");
                  setOutVendor(row?.vendor_name || "");
                  setOutVendorId(row?.vendor_id || "");
                }}
                items={outVendors}
                getKey={(v) => v.vendor_id || `name::${v.vendor_name}`}
                getLabel={(v) => v.vendor_code ? `${v.vendor_code} · ${v.vendor_name}` : v.vendor_name}
                getSubLabel={(v) => `${v.outstanding_count} bill${v.outstanding_count === 1 ? "" : "s"} outstanding${v.ambiguous ? " · ⚠ duplicate name — vendor link unresolved" : ""}`}
                getSearchText={(v) => `${v.vendor_code || ""} ${v.vendor_name || ""}`.toLowerCase()}
                placeholder="Search by vendor code or name…"
                allowClear
              />
            </div>
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Search Invoice / Voucher</div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                <input data-testid="bp-outstanding-search" value={outSearch} onChange={e => setOutSearch(e.target.value)}
                  placeholder="Vendor invoice no, voucher no, project…" disabled={!outVendor}
                  className="w-full pl-9 pr-8 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white disabled:bg-slate-100"/>
                {outSearch && <button onClick={() => setOutSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Outstanding</div>
              <div className="font-bold text-base text-emerald-700 tabular">{outVendor ? formatINR(vendorOutTotal) : "—"}</div>
              <div className="text-[10px] text-slate-500 tabular">{outVendor ? `${filteredOuts.length} bill${filteredOuts.length !== 1 ? "s" : ""}` : "select vendor"}</div>
            </div>
          </div>

          {!outVendor && (
            <div className="border border-slate-200 bg-white py-14 text-center text-slate-400 text-xs">
              Pick a vendor above to view their outstanding bills.
            </div>
          )}
          {outVendor && outLoading && (
            <div className="border border-slate-200 bg-white py-14 text-center text-slate-400 text-xs">Loading outstandings for <strong>{outVendor}</strong>…</div>
          )}
          {outVendor && !outLoading && filteredOuts.length === 0 && (
            <div className="border border-slate-200 bg-white py-14 text-center text-slate-400 text-xs">
              {outSearch
                ? <>No bills match &ldquo;<strong>{outSearch}</strong>&rdquo; for <strong>{outVendor}</strong>.</>
                : <>No outstanding bills for <strong>{outVendor}</strong>.</>}
            </div>
          )}
          {outVendor && !outLoading && filteredOuts.length > 0 && (
            <div className="border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>{["Source", "Voucher / Invoice", "Invoice / Ref No", "Date", "Project", "Branch", "Invoice", "GST", "TDS", "Net Payable"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3 whitespace-nowrap">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {filteredOuts.map(b => {
                    const isHold = !!b.is_hold_carry_forward;
                    return (
                      <tr key={b.id} className={`border-b border-slate-100 last:border-b-0 ${isHold ? "bg-amber-50/40" : ""}`}>
                        <td className="py-2 px-3">
                          {isHold
                            ? <span className="text-[10px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5">Hold Release</span>
                            : <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5">{MODULE_LABEL[b.source_module] || b.source_module}</span>}
                        </td>
                        <td className="py-2 px-3 font-bold text-slate-900">
                          {b.source_number}
                          {isHold && b.parent_voucher_number && (
                            <div className="text-[9px] font-normal text-amber-700 mt-0.5">From voucher {b.parent_voucher_number}</div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-slate-700 tabular">{b.vendor_ref_no || "—"}</td>
                        <td className="py-2 px-3 text-slate-600">{fmtDate(b.source_date)}</td>
                        <td className="py-2 px-3 text-slate-600">{b.project_name || "—"}</td>
                        <td className="py-2 px-3 text-slate-600">{b.branch_name || "—"}</td>
                        <td className="py-2 px-3 text-right tabular">{formatINR(b.invoice_amount)}</td>
                        <td className="py-2 px-3 text-right tabular">{formatINR(b.gst_amount)}</td>
                        <td className="py-2 px-3 text-right tabular text-rose-600">{formatINR(b.tds_amount)}</td>
                        <td className="py-2 px-3 text-right tabular font-bold text-emerald-700">{formatINR(b.net_payable)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={9} className="py-2 px-3 text-right font-bold text-slate-700 uppercase tracking-wider text-[10px]">Total Outstanding</td>
                    <td className="py-2 px-3 text-right tabular font-bold text-emerald-700 text-base">{formatINR(vendorOutTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "advances" && (<>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid="bp-adv-search" value={advSearch} onChange={e => setAdvSearch(e.target.value)}
              placeholder="Search advance #, vendor, purpose, batch…"
              className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
            {advSearch && <button onClick={() => setAdvSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
          </div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
            Showing {filteredAdv.length} of {advancesTotal} advance{advancesTotal !== 1 ? "s" : ""}
          </div>
          <ExportExcelButton endpoint="/exports/vendor-advances-excel"
            filename="vendor-advances.xlsx"
            label="Export Excel"
            testid="adv-export"/>
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>{["Advance #", "Date", "Vendor", "Purpose", "Amount", "Adjusted", "Balance", "Status", "Batch", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2 whitespace-nowrap">{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {filteredAdv.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">No advance payments yet. Click <strong>New Advance Payment</strong> to create one.</td></tr>
              )}
              {filteredAdv.map(a => {
                const balance = Math.max(0, Number(a.advance_amount || 0) - Number(a.total_adjusted || 0));
                return (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setViewingAdv(a)} data-testid={`bp-adv-row-${a.id}`}>
                    <td className="py-2 px-2 font-bold text-slate-900">{a.advance_number || "—"}</td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(a.advance_date)}</td>
                    <td className="py-2 px-2 text-slate-700">{a.vendor_name}</td>
                    <td className="py-2 px-2 text-slate-600">{a.purpose || "—"}</td>
                    <td className="py-2 px-2 text-right tabular font-bold">{formatINR(a.advance_amount)}</td>
                    <td className="py-2 px-2 text-right tabular text-orange-700">{a.total_adjusted > 0 ? formatINR(a.total_adjusted) : "—"}</td>
                    <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(balance)}</td>
                    <td className="py-2 px-2"><PendingApprovalBadge status={a.status} pendingInfo={advPendingMap[a.id]} /></td>
                    <td className="py-2 px-2 text-[10px] text-slate-500">{a.posted_batch_number || "—"}</td>
                    <td className="py-2 px-2 text-right" onClick={e => e.stopPropagation()}>
                      {advPendingMap[a.id] && (
                        <span className="mr-2 inline-block align-middle">
                          <TakeActionButton pendingInfo={advPendingMap[a.id]} testid={`take-action-adv-${a.id}`} />
                        </span>
                      )}
                      {(a.status === "Pending" || a.status === "Rejected") && (
                        <button onClick={async () => {
                          if (!window.confirm(`Delete advance ${a.advance_number || ""}?`)) return;
                          try { await api.delete(`/vendor-advances/${a.id}`); toast.success("Deleted"); loadAdvances(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
                        }} data-testid={`bp-adv-delete-${a.id}`} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 size={12}/></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={advancesPage} pageSize={advancesPageSize} total={advancesTotal}
          loading={advancesLoading}
          onPage={setAdvancesPage} onPageSize={setAdvancesPageSize}
        />
      </>)}
      </>}

      {showNew && formType === "bill_payment" && <NewVoucherForm key={`bpv-${newKey}`} refreshTick={outTick} onViewList={() => setShowNew(false)} onSwitchToAdvance={() => openNewForm("advance_payment")}
        onSaved={() => { loadVouchers(); loadAdvances(); setNewKey(k => k + 1); }}/>}
      {showNew && formType === "advance_payment" && <NewAdvanceForm key={`adv-${newKey}`} onViewList={() => { setShowNew(false); setTab("advances"); }} onSwitchToBPV={() => openNewForm("bill_payment")}
        onSaved={() => { loadAdvances(); loadVouchers(); setNewKey(k => k + 1); }}/>}
      {viewing && <VoucherDetailModal v={viewing} onClose={() => setViewing(null)}/>}
      {viewingAdv && <AdvanceDetailModal a={viewingAdv} onClose={() => setViewingAdv(null)}/>}
    </div>
  );
}

function NewVoucherForm({ onViewList, onSwitchToAdvance, onSaved, refreshTick = 0 }) {
  const navigate = useNavigate();
  const mastersRefreshKey = useMastersRefresh();
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendorKey, setVendorKey] = useState("");   // picker key (vendor_id or name:: fallback)
  const [voucherNumber, setVoucherNumber] = useState("");
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().slice(0, 10));
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState({});
  const [notes, setNotes] = useState("");
  const [actualBeneficiary, setActualBeneficiary] = useState("");
  const [saving, setSaving] = useState(false);
  const [billSearch, setBillSearch] = useState("");
  const [advBalance, setAdvBalance] = useState(null);   // { balance, advances: [...] }
  const [advAdjustments, setAdvAdjustments] = useState({}); // { vendor_advance_id: amount }

  useEffect(() => {
    api.get("/bill-payments/vendors-with-outstanding").then(r => setVendors(r.data || []));
  }, [mastersRefreshKey, refreshTick]);

  useEffect(() => {
    if (!vendorKey) { setOutstanding([]); setAdvBalance(null); setAdvAdjustments({}); return; }
    setLoading(true);
    Promise.all([
      // Jul-2026 — VENDOR IDENTITY FIX: outstandings fetched by vendor_id.
      api.get("/bill-payments/outstanding", { params: { vendor_id: vendorId, vendor_name: vendor } }),
      api.get("/vendor-advances/available-balance", { params: { vendor_name: vendor } }),
    ]).then(([rOut, rAdv]) => {
      setOutstanding(rOut.data || []);
      setSelected({});
      setAdvBalance(rAdv.data || null);
      // Auto-prefill adjustments: distribute available balance FIFO up to total net payable later
      const init = {};
      (rAdv.data?.advances || []).forEach(a => { init[a.id] = ""; });
      setAdvAdjustments(init);
    }).catch(e => toast.error(e?.response?.data?.detail || "Failed to load vendor data"))
      .finally(() => setLoading(false));
  }, [vendorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Jun-2026 — after an approval lands elsewhere (masters:refresh) or the
  // user returns to this tab, silently re-pull outstandings for the selected
  // vendor — but NEVER while bills are selected / adjustments typed (an
  // in-progress entry must not be wiped).
  const refreshSeenRef = useRef(mastersRefreshKey + refreshTick);
  useEffect(() => {
    const sig = mastersRefreshKey + refreshTick;
    if (sig === refreshSeenRef.current) return;
    refreshSeenRef.current = sig;
    if (!vendorKey) return;
    const hasWork = Object.values(selected).some(Boolean)
      || Object.values(advAdjustments).some(v => String(v || "").trim() !== "");
    if (hasWork) return;
    Promise.all([
      api.get("/bill-payments/outstanding", { params: { vendor_id: vendorId, vendor_name: vendor } }),
      api.get("/vendor-advances/available-balance", { params: { vendor_name: vendor } }),
    ]).then(([rOut, rAdv]) => {
      setOutstanding(rOut.data || []);
      setSelected({});
      setAdvBalance(rAdv.data || null);
      const init = {};
      (rAdv.data?.advances || []).forEach(a => { init[a.id] = ""; });
      setAdvAdjustments(init);
    }).catch(() => {});
  }, [mastersRefreshKey, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  function updateAdvAdjustment(id, val) {
    setAdvAdjustments(p => ({ ...p, [id]: val }));
  }
  function autoFillAdvAdjustment(maxToConsume) {
    // FIFO across available advances, capped at maxToConsume
    const next = {};
    let remaining = Number(maxToConsume) || 0;
    (advBalance?.advances || []).forEach(a => {
      const take = Math.min(a.balance, Math.max(0, remaining));
      next[a.id] = take > 0 ? take.toFixed(2) : "";
      remaining -= take;
    });
    setAdvAdjustments(next);
  }

  function toggleSelect(b) {
    setSelected(prev => {
      const next = { ...prev };
      if (next[b.id]) delete next[b.id];
      else { next[b.id] = { deductions: [], holds: [] }; setExpanded(e => ({ ...e, [b.id]: true })); }
      return next;
    });
  }
  function toggleExpand(id) { setExpanded(p => ({ ...p, [id]: !p[id] })); }
  function addDeduction(bid) {
    setSelected(p => { const cur = p[bid] || { deductions: [], holds: [] }; return { ...p, [bid]: { ...cur, deductions: [...cur.deductions, { type: "LD", amount: "", remarks: "" }] } }; });
  }
  function updateDeduction(bid, idx, patch) {
    setSelected(p => { const cur = p[bid]; const list = cur.deductions.slice(); list[idx] = { ...list[idx], ...patch }; return { ...p, [bid]: { ...cur, deductions: list } }; });
  }
  function removeDeduction(bid, idx) {
    setSelected(p => { const cur = p[bid]; return { ...p, [bid]: { ...cur, deductions: cur.deductions.filter((_, i) => i !== idx) } }; });
  }
  function addHold(bid) {
    setSelected(p => { const cur = p[bid] || { deductions: [], holds: [] }; return { ...p, [bid]: { ...cur, holds: [...cur.holds, { type: "GST Hold", amount: "", remarks: "" }] } }; });
  }
  function updateHold(bid, idx, patch) {
    setSelected(p => { const cur = p[bid]; const list = cur.holds.slice(); list[idx] = { ...list[idx], ...patch }; return { ...p, [bid]: { ...cur, holds: list } }; });
  }
  function removeHold(bid, idx) {
    setSelected(p => { const cur = p[bid]; return { ...p, [bid]: { ...cur, holds: cur.holds.filter((_, i) => i !== idx) } }; });
  }

  const totals = useMemo(() => {
    let inv = 0, gst = 0, tds = 0, ded = 0, hold = 0;
    outstanding.forEach(b => {
      if (!selected[b.id]) return;
      inv += Number(b.invoice_amount || 0);
      gst += Number(b.gst_amount || 0);
      tds += Number(b.tds_amount || 0);
      ded += selected[b.id].deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
      hold += selected[b.id].holds.reduce((s, h) => s + Number(h.amount || 0), 0);
    });
    const advAdj = Object.values(advAdjustments).reduce((s, v) => s + (Number(v) || 0), 0);
    const grossNet = inv + gst - tds - ded - hold;
    return { inv, gst, tds, ded, hold, advAdj, net: grossNet - advAdj };
  }, [outstanding, selected, advAdjustments]);

  const filteredOutstanding = useMemo(() => {
    if (!billSearch.trim()) return outstanding;
    const q = billSearch.trim().toLowerCase();
    return outstanding.filter(b => [b.source_number, b.vendor_ref_no, b.project_name, b.branch_name, b.parent_voucher_number]
      .some(s => (s || "").toString().toLowerCase().includes(q)));
  }, [outstanding, billSearch]);

  async function save() {
    if (!vendor) { toast.error("Pick a vendor"); return; }
    if (!vendorId) {
      toast.error("This vendor is not identity-linked (duplicate name or missing vendor code on the source voucher). Payment blocked to prevent paying the wrong bank account — verify the vendor code on the source voucher.");
      return;
    }
    if (!voucherDate) { toast.error("Voucher date is required"); return; }
    const lines = Object.entries(selected).map(([bid, info]) => ({
      bill_payment_id: bid,
      deductions: info.deductions.filter(d => Number(d.amount) > 0).map(d => ({ type: d.type, amount: Number(d.amount), remarks: d.remarks || "" })),
      holds: info.holds.filter(h => Number(h.amount) > 0).map(h => ({ type: h.type, amount: Number(h.amount), remarks: h.remarks || "", released: false })),
    }));
    if (lines.length === 0) { toast.error("Select at least one outstanding bill"); return; }
    const advance_adjustments = Object.entries(advAdjustments)
      .filter(([, v]) => Number(v) > 0)
      .map(([id, v]) => ({ vendor_advance_id: id, amount: Number(v), remarks: "" }));
    setSaving(true);
    try {
      const r = await api.post("/bill-payment-vouchers", { vendor_id: vendorId, vendor_name: vendor, voucher_date: voucherDate, notes, actual_beneficiary: actualBeneficiary, lines, advance_adjustments, voucher_number: voucherNumber || "" });
      const v = r.data;
      const msg = v.status === "Approved"
        ? `Voucher ${v.voucher_number} auto-approved (${formatINR(v.total_net_payable)})`
        : `Voucher ${v.voucher_number} submitted for approval`;
      toast.success(msg);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  const selCount = Object.keys(selected).length;

  return (
    <div className="bg-white border border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><FileText size={20} className="text-[#F97316]"/> New Bill Payment Voucher</h2>
        <div className="flex items-center gap-2">
          {onSwitchToAdvance && (
            <button onClick={onSwitchToAdvance} data-testid="bp-switch-to-advance-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-[#F97316] text-[#F97316] px-3 py-1.5 hover:bg-orange-50 bg-white">
              <Banknote size={13}/> Switch to Advance
            </button>
          )}
          <button onClick={onViewList} data-testid="bp-view-list-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 px-3 py-1.5 hover:bg-slate-100 bg-white">
            View List
          </button>
          <RefreshMastersButton testid="bp-refresh-masters-btn" />
        </div>
      </div>

      <div>
        <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-5 gap-4 border-b border-slate-200 bg-slate-50/40">
            <Field label="Voucher No (auto · admin can override)">
              <VoucherNumberInput
                module="bill_payment_voucher"
                value={voucherNumber}
                onChange={setVoucherNumber}
                testidPrefix="bp-voucher-number"
              />
            </Field>
            <Field label="Vendor *">
              {/* Jul-2026 — VENDOR IDENTITY FIX: keyed by vendor_id so two
                  vendors sharing a display name are two distinct entries.
                  The picked row's vendor_id drives outstanding fetch, the
                  BPV save and the frozen beneficiary snapshot. */}
              <SearchableSelect
                testid="bp-vendor-select"
                value={vendorKey}
                onChange={(key, row) => {
                  setVendorKey(key || "");
                  setVendor(row?.vendor_name || "");
                  setVendorId(row?.vendor_id || "");
                  if (row?.ambiguous) {
                    toast.warning("Multiple vendors share this name and this legacy bill has no vendor link — open the source voucher and re-save it with the vendor code, or contact admin.");
                  }
                }}
                items={vendors}
                getKey={(v) => v.vendor_id || `name::${v.vendor_name}`}
                getLabel={(v) => v.vendor_code ? `${v.vendor_code} · ${v.vendor_name}` : v.vendor_name}
                getSubLabel={(v) => `${v.outstanding_count} bill${v.outstanding_count === 1 ? "" : "s"} outstanding${v.ambiguous ? " · ⚠ duplicate name — vendor link unresolved" : ""}`}
                getSearchText={(v) => `${v.vendor_code || ""} ${v.vendor_name || ""}`.toLowerCase()}
                placeholder="Search by vendor code or name…"
                allowClear
              />
            </Field>
            <Field label="Voucher Date *">
              <input type="date" value={voucherDate} onChange={e => setVoucherDate(e.target.value)} data-testid="bp-voucher-date"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
            </Field>
            <Field label="Notes">
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. April payments cycle"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
            </Field>
            <Field label="Actual Beneficiary">
              <input value={actualBeneficiary} onChange={e => setActualBeneficiary(e.target.value)}
                placeholder="If paid to a third-party (factor / sister concern)…"
                data-testid="bp-actual-beneficiary"
                className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
            </Field>
          </div>

          {vendor && advBalance && advBalance.balance > 0 && (
            <div className="mx-6 mt-4 border border-amber-300 bg-amber-50" data-testid="bp-adv-balance-banner">
              <div className="flex items-center justify-between px-4 py-2 border-b border-amber-200 bg-amber-100/60">
                <div className="flex items-center gap-2 text-amber-900">
                  <AlertCircle size={16}/>
                  <span className="text-xs font-bold uppercase tracking-wider">Advance Outstanding for {vendor}</span>
                  <span className="text-xs">— available balance <strong className="tabular">{formatINR(advBalance.balance)}</strong> across {advBalance.advances.length} advance(s). Please adjust before raising payment.</span>
                </div>
                <button onClick={() => autoFillAdvAdjustment(Math.max(0, totals.inv + totals.gst - totals.tds - totals.ded - totals.hold))}
                  data-testid="bp-auto-adjust-advance"
                  className="text-[10px] uppercase font-bold tracking-wider bg-[#F97316] text-white px-3 py-1 hover:bg-[#EA580C]">
                  Auto-fill adjustment
                </button>
              </div>
              <div className="px-4 py-2">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-amber-900">
                      <th className="text-left py-1 pr-2 font-bold">Advance #</th>
                      <th className="text-left py-1 pr-2 font-bold">Date</th>
                      <th className="text-right py-1 pr-2 font-bold">Paid</th>
                      <th className="text-right py-1 pr-2 font-bold">Already Adjusted</th>
                      <th className="text-right py-1 pr-2 font-bold">Balance</th>
                      <th className="text-right py-1 pr-2 font-bold">Adjust Now</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advBalance.advances.map(a => (
                      <tr key={a.id} className="border-t border-amber-200/60">
                        <td className="py-1.5 pr-2 font-bold text-slate-900">{a.advance_number}</td>
                        <td className="py-1.5 pr-2 text-slate-600">{fmtDate(a.advance_date)}</td>
                        <td className="py-1.5 pr-2 text-right tabular">{formatINR(a.advance_amount)}</td>
                        <td className="py-1.5 pr-2 text-right tabular text-orange-700">{formatINR(a.total_adjusted)}</td>
                        <td className="py-1.5 pr-2 text-right tabular font-bold text-emerald-700">{formatINR(a.balance)}</td>
                        <td className="py-1.5 pr-2 text-right">
                          <input type="number" min="0" max={a.balance} step="0.01"
                            value={advAdjustments[a.id] || ""}
                            onChange={e => updateAdvAdjustment(a.id, e.target.value)}
                            data-testid={`bp-adv-adj-${a.id}`}
                            className="w-28 text-xs border border-amber-300 px-2 py-1 text-right focus:border-[#F97316] focus:outline-none bg-white"
                            placeholder="0.00"/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {vendor && advBalance && (advBalance.in_flight || []).length > 0 && (
            <div className="mx-6 mt-4 border border-indigo-200 bg-indigo-50 px-4 py-3" data-testid="bp-adv-inflight-banner">
              <div className="flex items-start gap-2 text-indigo-900">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <div className="text-xs font-bold uppercase tracking-wider mb-1">In-Flight Advances for {vendor}</div>
                  <div className="text-[11px] leading-relaxed text-indigo-800">
                    The following advance(s) are <strong>not yet released</strong> (cash hasn't actually left the bank). They will become available for adjustment only after their payment batch is <strong>marked Paid</strong> in <em>Post Payment</em>.
                  </div>
                  <table className="w-full text-xs mt-2">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-indigo-900">
                        <th className="text-left py-1 pr-2 font-bold">Advance #</th>
                        <th className="text-left py-1 pr-2 font-bold">Date</th>
                        <th className="text-right py-1 pr-2 font-bold">Amount</th>
                        <th className="text-left py-1 pr-2 font-bold">Status</th>
                        <th className="text-left py-1 pr-2 font-bold">Batch</th>
                        <th className="text-right py-1 pr-2 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advBalance.in_flight.map(a => (
                        <tr key={a.id} className="border-t border-indigo-200/60">
                          <td className="py-1.5 pr-2 font-bold text-slate-900">{a.advance_number}</td>
                          <td className="py-1.5 pr-2 text-slate-600">{fmtDate(a.advance_date)}</td>
                          <td className="py-1.5 pr-2 text-right tabular">{formatINR(a.advance_amount)}</td>
                          <td className="py-1.5 pr-2">{badge(a.status)}</td>
                          <td className="py-1.5 pr-2 text-indigo-700 tabular">{a.posted_batch_number || "—"}</td>
                          <td className="py-1.5 pr-2 text-right">
                            {a.posted_batch_id ? (
                              <button type="button" onClick={() => navigate("/purchase/post-payment")}
                                data-testid={`bp-adv-goto-batch-${a.id}`}
                                className="text-[10px] uppercase font-bold tracking-wider bg-indigo-600 text-white px-2.5 py-1 hover:bg-indigo-700 inline-block">
                                Go to Post Payment →
                              </button>
                            ) : (
                              <span className="text-[10px] text-indigo-500 italic">awaiting approval</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div className="px-6 py-4">
            {!vendor && <div className="py-10 text-center text-slate-400 text-sm">Select a vendor to load outstanding bills.</div>}
            {vendor && loading && <div className="py-10 text-center text-slate-400 text-sm">Loading outstandings…</div>}
            {vendor && !loading && outstanding.length === 0 && (
              <div className="py-10 text-center text-slate-400 text-sm">No outstanding bills for <strong>{vendor}</strong>.</div>
            )}
            {vendor && !loading && outstanding.length > 0 && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="relative flex-1 max-w-md">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input value={billSearch} onChange={e => setBillSearch(e.target.value)} data-testid="bp-bill-search"
                      placeholder="Search invoice / voucher number, project, branch…"
                      className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"/>
                    {billSearch && <button onClick={() => setBillSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={13}/></button>}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 ml-auto">
                    {filteredOutstanding.length} / {outstanding.length} bills
                  </div>
                </div>
              <div className="border border-slate-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>
                      <th className="w-8 py-3 px-2"></th>
                      <th className="w-8 py-3 px-2"></th>
                      {["Source", "Voucher / Invoice", "Invoice / Ref No", "Date", "Project", "Branch", "Invoice", "GST", "TDS", "Deductions", "Holds", "Net Payable"].map(h =>
                        <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2 whitespace-nowrap">{h}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutstanding.length === 0 && (
                      <tr><td colSpan={14} className="py-6 text-center text-slate-400 text-xs">No bills match &ldquo;<strong>{billSearch}</strong>&rdquo;</td></tr>
                    )}
                    {filteredOutstanding.map(b => {
                      const isSel = !!selected[b.id];
                      const info = selected[b.id] || { deductions: [], holds: [] };
                      const dedSum = info.deductions.reduce((s, d) => s + Number(d.amount || 0), 0);
                      const holdSum = info.holds.reduce((s, h) => s + Number(h.amount || 0), 0);
                      const net = Number(b.invoice_amount || 0) + Number(b.gst_amount || 0) - Number(b.tds_amount || 0) - dedSum - holdSum;
                      const exp = !!expanded[b.id];
                      const isHold = !!b.is_hold_carry_forward;
                      return (
                        <Fragment key={b.id}>
                          <tr className={`border-b border-slate-100 ${isSel ? "bg-orange-50/40" : isHold ? "bg-amber-50/40" : "hover:bg-slate-50/60"}`}>
                            <td className="py-2 px-2"><input type="checkbox" checked={isSel} onChange={() => toggleSelect(b)} data-testid={`bp-select-${b.id}`} className="cursor-pointer"/></td>
                            <td className="py-2 px-2">{isSel && <button onClick={() => toggleExpand(b.id)} data-testid={`bp-expand-${b.id}`} className="text-slate-500 hover:text-[#F97316] p-1">{exp ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</button>}</td>
                            <td className="py-2 px-2">
                              {isHold ? (
                                <span className="text-[10px] uppercase font-bold bg-amber-200 text-amber-900 px-1.5 py-0.5">Hold Release</span>
                              ) : (
                                <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5">{MODULE_LABEL[b.source_module] || b.source_module}</span>
                              )}
                            </td>
                            <td className="py-2 px-2 font-bold text-slate-900">
                              {b.source_number}
                              {isHold && b.parent_voucher_number && (
                                <div className="text-[9px] font-normal text-amber-700 mt-0.5">From voucher {b.parent_voucher_number}</div>
                              )}
                            </td>
                            <td className="py-2 px-2 text-slate-700 tabular">{b.vendor_ref_no || "—"}</td>
                            <td className="py-2 px-2 text-slate-600">{fmtDate(b.source_date)}</td>
                            <td className="py-2 px-2 text-slate-600">{b.project_name || "—"}</td>
                            <td className="py-2 px-2 text-slate-600">{b.branch_name || "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{formatINR(b.invoice_amount)}</td>
                            <td className="py-2 px-2 text-right tabular">{formatINR(b.gst_amount)}</td>
                            <td className="py-2 px-2 text-right tabular text-rose-600">{formatINR(b.tds_amount)}</td>
                            <td className="py-2 px-2 text-right tabular text-orange-700">{dedSum > 0 ? formatINR(dedSum) : "—"}</td>
                            <td className="py-2 px-2 text-right tabular text-amber-700">{holdSum > 0 ? formatINR(holdSum) : "—"}</td>
                            <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(net)}</td>
                          </tr>
                          {isSel && exp && (
                            <tr className="bg-slate-50/40 border-b-2 border-slate-200">
                              <td colSpan={14} className="px-6 py-3">
                                <DeductionsHoldsEditor info={info}
                                  onAddD={() => addDeduction(b.id)} onUpdD={(i, p) => updateDeduction(b.id, i, p)} onDelD={i => removeDeduction(b.id, i)}
                                  onAddH={() => addHold(b.id)} onUpdH={(i, p) => updateHold(b.id, i, p)} onDelH={i => removeHold(b.id, i)}
                                  prefix={`bp-${b.id}`}/>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 grid grid-cols-8 gap-3">
          <KPI label="Selected" value={`${selCount} bill(s)`} text/>
          <KPI label="Invoice" value={totals.inv}/>
          <KPI label="+ GST" value={totals.gst}/>
          <KPI label="− TDS" value={totals.tds} neg/>
          <KPI label="− Deductions" value={totals.ded} neg/>
          <KPI label="− Holds" value={totals.hold} neg/>
          <KPI label="− Advance" value={totals.advAdj} neg/>
          <KPI label="Net Payable" value={totals.net} accent/>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-white">
          <button onClick={() => navigate("/")} data-testid="bp-discard-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-600 hover:bg-slate-100">Discard</button>
          <button onClick={save} disabled={saving || selCount === 0} data-testid="bp-save-voucher"
            className="inline-flex items-center gap-2 bg-[#F97316] text-white px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
            <Save size={14}/> {saving ? "Saving…" : "Save & Submit for Approval"}
          </button>
        </div>
      </div>
  );
}

function DeductionsHoldsEditor({ info, onAddD, onUpdD, onDelD, onAddH, onUpdH, onDelH, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 flex items-center gap-1"><Coins size={11}/> Deductions ({info.deductions.length})</div>
          <button onClick={onAddD} data-testid={`${prefix}-add-deduction`} className="text-[10px] uppercase font-bold text-[#F97316] hover:text-[#EA580C] inline-flex items-center gap-1"><Plus size={11}/> Add</button>
        </div>
        {info.deductions.length === 0 && <div className="text-[11px] text-slate-400">No deductions.</div>}
        {info.deductions.map((d, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
            <select value={d.type} onChange={e => onUpdD(i, { type: e.target.value })} className="text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none">
              {DEDUCTION_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <input type="number" step="0.01" min="0" value={d.amount} onChange={e => onUpdD(i, { amount: e.target.value })} placeholder="Amount"
              data-testid={`${prefix}-deduction-amount-${i}`}
              className="w-28 text-xs border border-slate-300 px-2 py-1 text-right focus:border-[#F97316] focus:outline-none"/>
            <input value={d.remarks} onChange={e => onUpdD(i, { remarks: e.target.value })} placeholder="Remarks"
              className="flex-1 text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none"/>
            <button onClick={() => onDelD(i)} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 size={11}/></button>
          </div>
        ))}
      </div>
      <div className="border border-slate-200 bg-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 flex items-center gap-1"><ShieldAlert size={11}/> Holds ({info.holds.length})</div>
          <button onClick={onAddH} data-testid={`${prefix}-add-hold`} className="text-[10px] uppercase font-bold text-[#F97316] hover:text-[#EA580C] inline-flex items-center gap-1"><Plus size={11}/> Add</button>
        </div>
        {info.holds.length === 0 && <div className="text-[11px] text-slate-400">No holds.</div>}
        {info.holds.map((h, i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
            <select value={h.type} onChange={e => onUpdH(i, { type: e.target.value })} className="text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none">
              {HOLD_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <input type="number" step="0.01" min="0" value={h.amount} onChange={e => onUpdH(i, { amount: e.target.value })} placeholder="Amount"
              data-testid={`${prefix}-hold-amount-${i}`}
              className="w-28 text-xs border border-slate-300 px-2 py-1 text-right focus:border-[#F97316] focus:outline-none"/>
            <input value={h.remarks} onChange={e => onUpdH(i, { remarks: e.target.value })} placeholder="Remarks"
              className="flex-1 text-xs border border-slate-300 px-2 py-1 focus:border-[#F97316] focus:outline-none"/>
            <button onClick={() => onDelH(i)} className="text-rose-500 hover:text-rose-700 p-1"><Trash2 size={11}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function NewAdvanceForm({ onViewList, onSwitchToBPV, onSaved }) {
  const navigate = useNavigate();
  const mastersRefreshKey = useMastersRefresh();
  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [advanceNumber, setAdvanceNumber] = useState("");
  const [advanceDate, setAdvanceDate] = useState(new Date().toISOString().slice(0, 10));
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [actualBeneficiary, setActualBeneficiary] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingBalance, setExistingBalance] = useState(null);

  useEffect(() => {
    // Sprint 3.13 — use the lightweight non-paginated /vendors/picker
    // so the datalist sees ALL vendors (legacy /vendors caps at 200 and
    // silently hid older vendors from typeahead).
    api.get("/vendors/picker").then(r => setVendors((r.data || []).map(v => ({ id: v.id, name: v.name, vendor_code: v.vendor_code || "" }))));
  }, [mastersRefreshKey]);

  useEffect(() => {
    if (!vendor) { setExistingBalance(null); return; }
    api.get("/vendor-advances/available-balance", { params: { vendor_name: vendor } })
      .then(r => setExistingBalance(r.data))
      .catch(() => setExistingBalance(null));
  }, [vendor]);

  async function save() {
    if (!vendor) { toast.error("Pick a vendor"); return; }
    if (!vendorId) {
      toast.error("Select the vendor from the dropdown list (search by code or name, then click the entry) — typing alone doesn't link the vendor master.");
      return;
    }
    if (!advanceDate) { toast.error("Advance date is required"); return; }
    const amt = Number(advanceAmount || 0);
    if (amt <= 0) { toast.error("Advance amount must be greater than zero"); return; }
    setSaving(true);
    try {
      const r = await api.post("/vendor-advances", {
        vendor_id: vendorId, vendor_name: vendor, advance_date: advanceDate, advance_amount: amt,
        purpose, notes, actual_beneficiary: actualBeneficiary,
        advance_number: advanceNumber || "",
      });
      const a = r.data;
      const msg = a.status === "Approved"
        ? `Advance ${a.advance_number} approved — head to Post Payment → Vendor Advances to release the funds.`
        : `Advance ${a.advance_number} submitted for approval. On approval, post it from Post Payment → Vendor Advances.`;
      toast.success(msg);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white border border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Banknote size={20} className="text-[#F97316]"/> New Vendor Advance Payment</h2>
        <div className="flex items-center gap-2">
          {onSwitchToBPV && (
            <button onClick={onSwitchToBPV} data-testid="bp-switch-to-bpv-btn"
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 px-3 py-1.5 hover:bg-slate-100 bg-white">
              <FileText size={13}/> Switch to Bill Payment
            </button>
          )}
          <button onClick={onViewList} data-testid="bp-adv-view-list-btn"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 px-3 py-1.5 hover:bg-slate-100 bg-white">
            View List
          </button>
          <RefreshMastersButton testid="bp-adv-refresh-masters-btn" />
        </div>
      </div>

      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-4 gap-4 border-b border-slate-200 bg-slate-50/40">
        <Field label="Advance No (auto · admin can override)">
          <VoucherNumberInput
            module="vendor_advance"
            value={advanceNumber}
            onChange={setAdvanceNumber}
            testidPrefix="bp-adv-number"
          />
        </Field>        <Field label="Vendor *">
          {/* Sprint 2026-Feb-10 — SearchableSelect with code + name */}
          <SearchableSelect
            testid="bp-adv-vendor-select"
            value={vendor}
            onChange={(name, row) => { setVendor(name || ""); setVendorId(row?.id || ""); }}
            items={vendors}
            getKey={(v) => v.name}
            getLabel={(v) => v.vendor_code ? `${v.vendor_code} · ${v.name}` : v.name}
            getSearchText={(v) => `${v.vendor_code || ""} ${v.name || ""}`.toLowerCase()}
            placeholder="Search by vendor code or name…"
            allowClear
          />
        </Field>
        <Field label="Advance Date *">
          <input type="date" value={advanceDate} onChange={e => setAdvanceDate(e.target.value)} data-testid="bp-adv-date"
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Advance Amount *">
          <input type="number" step="0.01" min="0" value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} data-testid="bp-adv-amount"
            placeholder="0.00"
            className="w-full text-xs border border-slate-300 px-2 py-1.5 text-right focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Bank Ledger (chosen at Post Payment)">
          <div className="text-[11px] text-slate-500 italic bg-slate-100 border border-slate-200 px-2 py-1.5 leading-relaxed">
            Source bank is selected when you post the approved advance from <strong>Post Payment → Vendor Advances</strong>.
          </div>
        </Field>
        <Field label="Purpose">
          <input value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="e.g. Material Advance for PO/2026/0001" data-testid="bp-adv-purpose"
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Notes">
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal remarks"
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
        <Field label="Actual Beneficiary">
          <input value={actualBeneficiary} onChange={e => setActualBeneficiary(e.target.value)}
            placeholder="If paid to a third-party (factor / sister concern)…"
            data-testid="bp-adv-actual-beneficiary"
            className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
        </Field>
      </div>

      {vendor && existingBalance && existingBalance.balance > 0 && (
        <div className="mx-6 mt-4 border border-amber-300 bg-amber-50 px-4 py-2 flex items-center gap-2 text-amber-900 text-xs" data-testid="bp-adv-existing-balance">
          <AlertCircle size={14}/>
          <span><strong>{vendor}</strong> already has an outstanding advance of <strong className="tabular">{formatINR(existingBalance.balance)}</strong> ({existingBalance.advances.length} advance(s)). Consider adjusting from existing balance before raising a new advance.</span>
        </div>
      )}

      <div className="px-6 py-6">
        <div className="bg-orange-50 border border-orange-200 px-4 py-3 text-[11px] text-orange-900">
          <strong className="uppercase tracking-wider text-[10px]">Workflow:</strong> Once submitted, this advance goes through the approval matrix. On final approval, it appears in <em>Post Payment → Vendor Advances</em> where the accountant picks the source bank and posts it into a payment batch. Once the batch is marked Paid, the advance balance becomes available for adjustment in future Bill Payment Vouchers for {vendor || "this vendor"}.
        </div>
      </div>

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 grid grid-cols-3 gap-3">
        <KPI label="Vendor" value={vendor || "—"} text/>
        <KPI label="Advance Amount" value={Number(advanceAmount) || 0} accent/>
        <KPI label="Existing Balance" value={existingBalance?.balance || 0} text={false}/>
      </div>

      <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-white">
        <button onClick={() => navigate("/")} data-testid="bp-adv-discard-btn"
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-600 hover:bg-slate-100">Discard</button>
        <button onClick={save} disabled={saving || !vendor || !Number(advanceAmount)} data-testid="bp-adv-save"
          className="inline-flex items-center gap-2 bg-[#F97316] text-white px-5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
          <Save size={14}/> {saving ? "Saving…" : "Save & Submit for Approval"}
        </button>
      </div>
    </div>
  );
}


function AdvanceDetailModal({ a, onClose }) {
  const balance = Math.max(0, Number(a.advance_amount || 0) - Number(a.total_adjusted || 0));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-[1100px] max-h-[95vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Banknote size={20} className="text-[#F97316]"/> {a.advance_number || "(no number)"} — {a.vendor_name}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              {badge(a.status)}
              <span>{fmtDate(a.advance_date)}</span>
              {a.posted_batch_number && <span className="text-indigo-700">Batch: {a.posted_batch_number}</span>}
              {a.bank_ledger_name && <span className="text-indigo-700">Bank: {a.bank_ledger_name}</span>}
              {a.paid_at && <span className="text-emerald-700">Paid: {fmtDate(a.paid_at)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bp-adv-detail-close"><X size={20}/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <KPI label="Advance Paid" value={a.advance_amount} accent/>
            <KPI label="Adjusted" value={a.total_adjusted}/>
            <KPI label="Balance" value={balance}/>
          </div>

          {a.purpose && <div className="text-xs text-slate-600"><strong>Purpose:</strong> {a.purpose}</div>}
          {a.actual_beneficiary && <div className="text-xs text-slate-600" data-testid="adv-detail-actual-beneficiary"><strong>Actual Beneficiary:</strong> {a.actual_beneficiary}</div>}
          {a.notes && <div className="text-xs text-slate-600"><strong>Notes:</strong> {a.notes}</div>}

          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Adjustments ({(a.adjustments || []).length})</div>
            {(a.adjustments || []).length === 0 ? (
              <div className="text-xs text-slate-400 border border-slate-200 bg-white px-4 py-3">No adjustments yet.</div>
            ) : (
              <div className="border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b-2 border-slate-200">
                    <tr>{["BPV Voucher", "Amount", "Adjusted On", "Adjusted By", "Remarks"].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {(a.adjustments || []).map((adj, i) => (
                      <tr key={adj.id || i} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-2 px-3 font-bold text-slate-900">{adj.voucher_number || "—"}</td>
                        <td className="py-2 px-3 text-right tabular text-orange-700 font-bold">{formatINR(adj.amount)}</td>
                        <td className="py-2 px-3 text-slate-600 tabular">{(adj.adjusted_at || "").slice(0, 19).replace("T", " ")}</td>
                        <td className="py-2 px-3 text-slate-600">{adj.adjusted_by}</td>
                        <td className="py-2 px-3 text-slate-600">{adj.remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {(a.audit || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1"><History size={11}/> Audit Trail</div>
              <div className="border border-slate-200 bg-white">
                {(a.audit || []).slice().reverse().map((au, i) => (
                  <div key={i} className="text-[11px] flex gap-3 py-1.5 px-3 border-b border-slate-100 last:border-b-0">
                    <span className="text-slate-400 w-36 tabular">{(au.at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className="font-bold w-32 text-slate-700">{au.action}</span>
                    <span className="flex-1 text-slate-600">{au.details}</span>
                    <span className="text-slate-400">{au.user}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function VoucherDetailModal({ v, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 overflow-y-auto">
      <div className="bg-white border border-slate-200 shadow-xl w-full max-w-[1300px] max-h-[95vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><FileText size={20} className="text-[#F97316]"/> {v.voucher_number || "(no number)"} — {v.vendor_name}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              {badge(v.status)}
              <span>{fmtDate(v.voucher_date)}</span>
              {v.branch_name && <span className="inline-flex items-center gap-1"><Building2 size={10}/>{v.branch_name}</span>}
              {v.posted_batch_number && <span className="text-indigo-700">Batch: {v.posted_batch_number}</span>}
              {v.bank_ledger_name && <span className="text-indigo-700">Bank: {v.bank_ledger_name}</span>}
              {v.payment_reference_no && <span className="text-indigo-700">Ref: {v.payment_reference_no}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {v.status === "Paid" && (
              <button onClick={() => downloadPaymentAdvice(v)} data-testid="bp-detail-advice-btn"
                className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-emerald-600 text-emerald-700 bg-white px-3 py-1.5 hover:bg-emerald-50">
                <FileDown size={13}/> Payment Advice (PDF)
              </button>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="bp-detail-close"><X size={20}/></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Source", "Voucher / Invoice", "Invoice / Ref No", "Date", "Project", "Invoice", "GST", "TDS", "Deductions", "Holds", "Net Payable"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-2">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(v.lines || []).map((ln, i) => (
                  <tr key={i} className="border-b border-slate-100 align-top">
                    <td className="py-2 px-2"><span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5">{MODULE_LABEL[ln.source_module] || ln.source_module}</span></td>
                    <td className="py-2 px-2 font-bold text-slate-900">{ln.source_number}</td>
                    <td className="py-2 px-2 text-slate-700 tabular">{ln.vendor_ref_no || "—"}</td>
                    <td className="py-2 px-2 text-slate-600">{fmtDate(ln.source_date)}</td>
                    <td className="py-2 px-2 text-slate-600">{ln.project_name || "—"}</td>
                    <td className="py-2 px-2 text-right tabular">{formatINR(ln.invoice_amount)}</td>
                    <td className="py-2 px-2 text-right tabular">{formatINR(ln.gst_amount)}</td>
                    <td className="py-2 px-2 text-right tabular text-rose-600">{formatINR(ln.tds_amount)}</td>
                    <td className="py-2 px-2 text-orange-700">
                      {(ln.deductions || []).length === 0 ? "—" : (ln.deductions || []).map((d, j) =>
                        <div key={j} className="text-[10px]"><strong>{d.type}</strong> {formatINR(d.amount)} {d.remarks && <span className="text-slate-500">— {d.remarks}</span>}</div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-amber-700">
                      {(ln.holds || []).length === 0 ? "—" : (ln.holds || []).map((h, j) => (
                        <div key={j} className={`text-[10px] ${h.released ? "opacity-60" : ""}`}>
                          <strong>{h.type}</strong> {formatINR(h.amount)} {h.remarks && <span className="text-slate-500">— {h.remarks}</span>}
                          {h.released && <span className="ml-1 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800 px-1 py-0.5">Carried Forward</span>}
                        </div>
                      ))}
                    </td>
                    <td className="py-2 px-2 text-right tabular font-bold text-emerald-700">{formatINR(ln.net_payable)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td colSpan={5} className="py-2 px-2 text-right font-bold text-slate-700 uppercase tracking-wider text-[10px]">Totals</td>
                  <td className="py-2 px-2 text-right tabular font-bold">{formatINR(v.total_invoice)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold">{formatINR(v.total_gst)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold text-rose-600">{formatINR(v.total_tds)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold text-orange-700">{formatINR(v.total_deductions)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold text-amber-700">{formatINR(v.total_holds)}</td>
                  <td className="py-2 px-2 text-right tabular font-bold text-emerald-700 text-base">{formatINR(v.total_net_payable)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {Number(v.total_advance_adjusted || 0) > 0 && (
            <div className="bg-orange-50 border border-orange-200 px-4 py-2 text-[11px] text-orange-900" data-testid="bp-voucher-adv-adj-summary">
              <div className="font-bold uppercase tracking-wider text-[10px] mb-1 flex items-center gap-1"><Banknote size={11}/> Advance Adjustments — ₹{Number(v.total_advance_adjusted).toLocaleString("en-IN")}</div>
              {(v.advance_adjustments || []).map((adj, i) => (
                <div key={adj.id || i} className="text-[11px]"><strong>{adj.advance_number}</strong> · {formatINR(adj.amount)} {adj.remarks && <span className="text-slate-500">— {adj.remarks}</span>}</div>
              ))}
            </div>
          )}

          {v.status === "Posted" && v.total_holds > 0 && (
            <div className="bg-amber-50 border border-amber-200 px-4 py-2 text-[11px] text-amber-800 flex items-center gap-2">
              <ShieldAlert size={13}/>
              <span>Holds (₹{Number(v.total_holds).toLocaleString("en-IN")}) have been auto-carried-forward as <strong>Hold Release</strong> outstandings against {v.vendor_name}. Create a new Bill Payment Voucher when ready to release them.</span>
            </div>
          )}

          {v.actual_beneficiary && <div className="text-xs text-slate-600" data-testid="bpv-detail-actual-beneficiary"><strong>Actual Beneficiary:</strong> {v.actual_beneficiary}</div>}
          {v.notes && <div className="text-xs text-slate-600"><strong>Notes:</strong> {v.notes}</div>}

          {(v.audit || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1"><History size={11}/> Audit Trail</div>
              <div className="border border-slate-200 bg-white">
                {(v.audit || []).slice().reverse().map((a, i) => (
                  <div key={i} className="text-[11px] flex gap-3 py-1.5 px-3 border-b border-slate-100 last:border-b-0">
                    <span className="text-slate-400 w-36 tabular">{a.at?.slice(0, 19).replace("T", " ")}</span>
                    <span className="font-bold w-32 text-slate-700">{a.action}</span>
                    <span className="flex-1 text-slate-600">{a.details}</span>
                    <span className="text-slate-400">{a.user}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function KPI({ label, value, neg, accent, text }) {
  return (
    <div className={`border ${accent ? "border-[#F97316] bg-orange-50" : "border-slate-200 bg-white"} px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`text-xs font-bold tabular ${accent ? "text-[#F97316]" : neg ? "text-rose-700" : "text-slate-900"}`}>{text ? value : formatINR(value)}</div>
    </div>
  );
}
